<template>
  <div class="container-fluid py-4">
    <div class="mb-3">
      <h2>Activity Log</h2>
      <p class="text-muted mb-0">
        The audit trail of who did what, newest first. Click an actor, company,
        activity or linked record to narrow the list to it.
      </p>
    </div>
    <p v-if="!loaded" class="text-center py-5" role="status">
      Loading activity…
    </p>
    <div v-else-if="!isSuperAdmin" class="alert alert-warning">
      This page requires super admin access.
    </div>
    <template v-else>
      <ActivityLogFiltersComponent v-model="filters" :activityKeys />
      <p v-if="status === 'loading'" class="text-center py-5" role="status">
        Loading activity…
      </p>
      <div
        v-else-if="status === 'error'"
        class="alert alert-warning mt-3"
        role="alert"
      >
        Could not load activity.
        <button class="btn btn-link" @click="load">Try again</button>
      </div>
      <ActivityLogTableComponent
        v-else
        :rows
        :hasMore="nextBeforeId !== null"
        :loadingMore
        :moreFailed
        @filter="narrow"
        @more="loadMore"
      />
    </template>
  </div>
</template>
<script setup lang="ts">
import { computed, onBeforeUnmount, ref, shallowRef, watch } from "vue";
import { useAccount } from "@/composables/account/useAccount";
import { reachesAccessClass } from "@/composables/useAdminCheck";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import ActivityLogFiltersComponent from "./ActivityLogFiltersComponent.vue";
import ActivityLogTableComponent from "./ActivityLogTableComponent.vue";
import { listActivities, listActivityKeys } from "./activityLogApi";
import type { ActivityLogEntry, ActivityLogFilters } from "./activityLogTypes";
import { useActivityLogQuery } from "./useActivityLogQuery";

const PAGE_SIZE = 50;

const { globalErrorHandler } = useGlobalHandlers();
const { filters } = useActivityLogQuery();
const activityKeys = ref<string[]>([]);
const rows = shallowRef<ActivityLogEntry[]>([]);
const status = ref<"loading" | "ready" | "error">("loading");
const nextBeforeId = ref<number | null>(null);
const loadingMore = ref(false);
const moreFailed = ref(false);
const { accountData, loaded } = useAccount((account) => {
  if (!reachesAccessClass(account, "SUPER")) return;
  listActivityKeys()
    .then((keys) => (activityKeys.value = keys))
    .catch(globalErrorHandler);
});

const isSuperAdmin = computed(
  () => loaded.value && reachesAccessClass(accountData, "SUPER")
);
// One controller per filter snapshot: a filter change aborts the first page and any "load more" of the old snapshot.
let controller = new AbortController();

onBeforeUnmount(() => controller.abort());

watch(
  [filters, isSuperAdmin],
  ([, allowed]) => {
    if (allowed) void load();
  },
  { immediate: true }
);

async function load() {
  controller.abort();
  controller = new AbortController();
  const { signal } = controller;
  status.value = "loading";
  loadingMore.value = false;
  moreFailed.value = false;
  try {
    const page = await listActivities(
      { ...filters.value, limit: PAGE_SIZE },
      signal
    );
    if (signal.aborted) return;
    rows.value = page.items;
    nextBeforeId.value = page.next_before_id;
    status.value = "ready";
  } catch (error) {
    if (signal.aborted) return;
    status.value = "error";
    globalErrorHandler(error);
  }
}

async function loadMore() {
  if (nextBeforeId.value === null) return;
  const { signal } = controller;
  loadingMore.value = true;
  moreFailed.value = false;
  try {
    const page = await listActivities(
      { ...filters.value, limit: PAGE_SIZE, before_id: nextBeforeId.value },
      signal
    );
    if (signal.aborted) return;
    rows.value = [...rows.value, ...page.items];
    nextBeforeId.value = page.next_before_id;
  } catch (error) {
    if (signal.aborted) return;
    moreFailed.value = true;
    globalErrorHandler(error);
  } finally {
    if (!signal.aborted) loadingMore.value = false;
  }
}

function narrow(patch: ActivityLogFilters) {
  filters.value = { ...filters.value, ...patch };
}
</script>
